import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Search, Download, Clock } from 'lucide-react';
import * as XLSX from 'xlsx';
import { formatCurrency, SortableHeader, sortAndFilter } from './helpers';

export const MaterialTab = ({ material, pending, data }) => {
  const [sort, setSort] = useState({ field: 'invoiceDate', dir: 'desc' });
  const [filter, setFilter] = useState('');

  const sortedEntries = sortAndFilter(material?.entries, sort, filter, ['vendorName', 'type', 'invoiceDate']);
  const pendingMaterialItems = pending?.entries?.filter(e => e.type === 'MATERIAL' || e.type === 'SUBCONTRACTOR') || [];

  const exportToExcel = () => {
    if (!material?.entries?.length) return;
    const exportData = material.entries.map(entry => ({
      'Invoice Date': entry.invoiceDate, 'Vendor': entry.vendorName, 'Type': entry.type,
      'Amount': entry.amount, 'Markup': entry.markup, 'Total': entry.totalCharge
    }));
    exportData.push({ 'Invoice Date': 'TOTAL', 'Vendor': '', 'Type': '', 'Amount': '', 'Markup': '', 'Total': material.total || 0 });
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Material');
    XLSX.writeFile(wb, `${data.job.jobNumber}_material_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <Card className='border-slate-200 shadow-sm rounded-sm'>
      <CardHeader>
        <div className='flex items-center justify-between flex-wrap gap-4'>
          <CardTitle className='text-xl font-bold text-slate-800'>
            Material / Sub / Equipment Breakdown
            <span className='ml-2 text-lg font-normal text-slate-500'>Total: {formatCurrency(material.total)}</span>
          </CardTitle>
          <div className='flex items-center gap-2'>
            <div className='relative'>
              <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400' />
              <Input placeholder='Search material...' value={filter} onChange={(e) => setFilter(e.target.value)}
                className='pl-9 w-48 rounded-sm' data-testid="material-search" />
            </div>
            <Button variant='outline' size='sm' onClick={exportToExcel} disabled={!material?.entries?.length}
              className='rounded-sm border-2 border-[#1A3668] text-[#1A3668] hover:bg-[#1A3668] hover:text-white'
              data-testid="export-job-material-excel">
              <Download className='w-4 h-4 mr-2' />Export
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {pendingMaterialItems.length > 0 && (
          <div className='mb-4 p-3 bg-amber-50 border border-amber-200 rounded-sm'>
            <p className='text-sm font-bold text-amber-800 mb-2'>
              <Clock className='w-4 h-4 inline mr-1' />Pending Material/Sub Items ({pendingMaterialItems.length})
            </p>
            <div className='space-y-1'>
              {pendingMaterialItems.map(entry => (
                <div key={entry.id} className='flex justify-between text-sm'>
                  <span className='text-amber-700'>
                    <Badge variant='outline' className='text-xs mr-2 bg-amber-100'>PENDING</Badge>
                    {entry.vendor} - {entry.poNumber}
                  </span>
                  <span className='font-mono text-amber-800'>{formatCurrency(entry.totalWithMarkup)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className='overflow-x-auto'>
          <table className='w-full border-collapse text-sm'>
            <thead>
              <tr className='bg-slate-100 border-b-2 border-slate-200'>
                <SortableHeader field='invoiceDate' label='Date' sortState={sort} setSortState={setSort} />
                <SortableHeader field='vendorName' label='Vendor' sortState={sort} setSortState={setSort} />
                <th className='text-left p-2 text-xs font-bold uppercase tracking-wider text-slate-600'>Invoice #</th>
                <SortableHeader field='type' label='Type' sortState={sort} setSortState={setSort} />
                <SortableHeader field='amount' label='Amount' sortState={sort} setSortState={setSort} align='right' />
                <th className='text-right p-2 text-xs font-bold uppercase tracking-wider text-slate-600'>Markup %</th>
                <SortableHeader field='totalCost' label='Total' sortState={sort} setSortState={setSort} align='right' />
              </tr>
            </thead>
            <tbody>
              {sortedEntries.map((entry) => (
                <tr key={entry.id} className='border-b border-slate-200 hover:bg-slate-50'>
                  <td className='p-2 font-mono text-slate-600'>{entry.invoiceDate}</td>
                  <td className='p-2 font-medium text-slate-700'>{entry.vendorName}</td>
                  <td className='p-2 font-mono text-slate-600'>{entry.invoiceNumber}</td>
                  <td className='p-2'>
                    <Badge className={`rounded-sm text-xs ${
                      entry.type === 'MATERIAL' ? 'bg-blue-100 text-blue-800' :
                      entry.type === 'EQUIPMENT' ? 'bg-orange-100 text-orange-800' :
                      'bg-purple-100 text-purple-800'
                    }`}>{entry.type}</Badge>
                  </td>
                  <td className='p-2 text-right font-mono text-slate-700'>{formatCurrency(entry.amount)}</td>
                  <td className='p-2 text-right font-mono text-amber-600'>{entry.markupPct}%</td>
                  <td className='p-2 text-right font-mono font-bold text-[#16A34A]'>{formatCurrency(entry.totalCost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {sortedEntries.length === 0 && (
            <div className='text-center p-8 text-slate-500'>
              {filter ? 'No matching material entries' : 'No material entries for this job'}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
